import { useEffect, useRef, useState, useCallback } from 'react';
import { getWsTicket } from '../utils/wsTicket';

const WS_BASE = process.env.REACT_APP_BACKEND_URL
  ?.replace('https://', 'wss://')
  ?.replace('http://', 'ws://');

const RECONNECT_DELAY = 3000;
const PING_INTERVAL = 25000;

export function useChatWebSocket({ conversationId, userId, userType, userName, enabled = true }) {
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);

  // Callback refs (updated by consumer)
  const onMessageRef = useRef(null);
  const onTypingRef = useRef(null);
  const onReadRef = useRef(null);

  const cleanup = useCallback(() => {
    if (pingRef.current) clearInterval(pingRef.current);
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
    if (wsRef.current) {
      wsRef.current.onclose = null; // prevent reconnect on intentional close
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setOnlineUsers([]);
    setTypingUsers([]);
  }, []);

  const connect = useCallback(async () => {
    if (!conversationId || !userId || !userType || !enabled || !WS_BASE) return;
    cleanup();

    // SEC-003 — Auth WS via ticket court-vie (cf. utils/wsTicket.js)
    const ticket = await getWsTicket();
    if (!ticket) return;
    const url = `${WS_BASE}/api/ws/chat/${conversationId}?user_id=${userId}&user_type=${userType}&token=${encodeURIComponent(ticket)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      // Start ping
      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, PING_INTERVAL);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case 'new_message':
            onMessageRef.current?.(data.data);
            break;
          case 'user_typing':
            if (data.user_id !== userId) {
              onTypingRef.current?.(data.user_id, data.is_typing);
              setTypingUsers(prev => {
                if (data.is_typing) {
                  return prev.includes(data.user_id) ? prev : [...prev, data.user_id];
                }
                return prev.filter(u => u !== data.user_id);
              });
            }
            break;
          case 'message_read':
            onReadRef.current?.(data.message_id, data.by_user_id);
            break;
          case 'user_joined':
            setOnlineUsers(prev => [...prev.filter(u => u.user_id !== data.user_id), { user_id: data.user_id, user_type: data.user_type }]);
            break;
          case 'user_left':
            setOnlineUsers(prev => prev.filter(u => u.user_id !== data.user_id));
            break;
          case 'connection_confirmed':
            setOnlineUsers(data.online_users || []);
            break;
          default:
            break;
        }
      } catch { /* ignore parse errors */ }
    };

    ws.onclose = () => {
      setIsConnected(false);
      if (pingRef.current) clearInterval(pingRef.current);
      // Auto-reconnect
      reconnectRef.current = setTimeout(connect, RECONNECT_DELAY);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [conversationId, userId, userType, enabled, cleanup]);

  useEffect(() => {
    connect();
    return cleanup;
  }, [connect, cleanup]);

  const sendMessage = useCallback((content) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        content,
        sender_name: userName || userId
      }));
      return true;
    }
    return false;
  }, [userName, userId]);

  const sendTyping = useCallback((isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: isTyping }));
    }
  }, []);

  const markRead = useCallback((messageId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'read', message_id: messageId }));
    }
  }, []);

  return {
    isConnected,
    onlineUsers,
    typingUsers,
    sendMessage,
    sendTyping,
    markRead,
    onMessageRef,
    onTypingRef,
    onReadRef,
  };
}
